import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Default data for initial render
const defaultAcademyInfo = {
  name: "The Coppini Academy of Fine Art",
  mission: "The Coppini Academy of Fine Art is dedicated to the promotion and preservation of representational art through educational programs, demonstrations, and workshops.",
  history: "Founded in 1945, the academy has been a cornerstone of fine art education in San Antonio.",
  extendedHistory: {
    founder: { name: "Pompeo Luigi Coppini", years: "1870-1957", bio: "", imageUrl: "" },
    protege: { name: "Waldine Tauch", years: "1903-1986", bio: "", imageUrl: "" },
    milestones: []
  }
};

const defaultLeadershipBoard = {
  executiveBoard: [],
  chairPositions: [],
  instructorSchedule: []
};

export const About = () => {
  const [academyInfo, setAcademyInfo] = useState(defaultAcademyInfo);
  const [leadershipBoard, setLeadershipBoard] = useState(defaultLeadershipBoard);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoRes, leadershipRes] = await Promise.all([
          fetch(`${API_URL}/api/content/academy-info`),
          fetch(`${API_URL}/api/content/leadership`)
        ]);
        if (infoRes.ok) setAcademyInfo(await infoRes.json());
        if (leadershipRes.ok) setLeadershipBoard(await leadershipRes.json());
      } catch (err) {
        console.error('Error fetching about data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <SEOHead
        title="About Us"
        description="Learn about The Coppini Academy of Fine Art, San Antonio's classical art school founded in 1945. Meet our leadership, explore our history, and discover our mission to preserve representational art."
        canonicalPath="/about"
      />
      <section className="bg-gradient-to-b from-stone-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-stone-900 mb-6">About Us</h1>
          <p className="text-xl text-stone-600">
            Preserving classical art traditions for over 80 years
          </p>
        </div>
      </section>

      {/* Academy Building Photo */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_13381205-307a-4c7d-8288-c75d39e5acae/artifacts/4vthlr1b_Coppini-Academy-of-Fine-Arts.jpg" 
            alt="The Coppini Academy of Fine Art building — Celebrating 80 Years (1945–2025)"
            className="w-full rounded-lg shadow-lg"
          />
        </div>
      </section>

      {/* Mission */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-6 text-center">Our Mission</h2>
          <p className="text-lg text-stone-700 leading-relaxed mb-8">
            {academyInfo.mission}
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Our Values</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Excellence in Teaching</h3>
              <p className="text-stone-700 leading-relaxed">
                We maintain the highest standards of classical art education, with experienced instructors who are dedicated to nurturing each student's potential.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Community & Collaboration</h3>
              <p className="text-stone-700 leading-relaxed">
                Our academy fosters a supportive community where artists of all levels can learn, grow, and share their passion for fine art.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">Classical Tradition</h3>
              <p className="text-stone-700 leading-relaxed">
                We honor time-tested methods while encouraging students to develop their unique artistic voice within the classical framework.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      {leadershipBoard.executiveBoard?.length > 0 && (
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-serif text-stone-900 mb-4 text-center">Leadership</h2>
            <p className="text-stone-600 text-center mb-12">2026 Executive Board</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {leadershipBoard.executiveBoard.map((member, index) => {
                const isOpen = member.name === 'Open';
                const isAvailable = member.name === 'Position Available';
                return (
                <div 
                  key={index} 
                  className={`p-4 rounded-lg border ${(isOpen || isAvailable) ? 'bg-stone-50 border-stone-200' : 'bg-white border-stone-200 hover:shadow-md transition-shadow'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cafa-red-100 flex items-center justify-center">
                      <Users size={18} className="text-cafa-red" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">
                        {isOpen ? (
                          <span className="text-stone-400 italic">Position Open</span>
                        ) : isAvailable ? (
                          <a
                            href={`mailto:info@thecoppini.org?subject=Interested in ${encodeURIComponent(member.role)}`}
                            className="text-cafa-red italic underline decoration-dotted underline-offset-2 hover:text-cafa-red-900"
                            data-testid={`position-available-exec-${index}`}
                          >
                            Position Available
                          </a>
                        ) : (
                          member.name
                        )}
                        {member.note && <span className="text-stone-500 font-normal text-sm"> ({member.note})</span>}
                      </p>
                      <p className="text-sm text-cafa-red">{member.role}</p>
                      {member.duties && <p className="text-xs text-stone-500 mt-1">{member.duties}</p>}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Chair Positions */}
            {leadershipBoard.chairPositions?.length > 0 && (
              <>
                <h3 className="text-2xl font-serif text-stone-900 mb-6 text-center">Chair Positions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                  {leadershipBoard.chairPositions.map((member, index) => {
                    const isAvailable = member.name === 'Position Available';
                    return (
                    <div 
                      key={index} 
                      className="p-4 rounded-lg border bg-white border-stone-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                          <Users size={18} className="text-stone-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-stone-900">
                            {isAvailable ? (
                              <a
                                href={`mailto:info@thecoppini.org?subject=Interested in ${encodeURIComponent(member.role)}`}
                                className="text-cafa-red italic underline decoration-dotted underline-offset-2 hover:text-cafa-red-900"
                                data-testid={`position-available-chair-${index}`}
                              >
                                Position Available
                              </a>
                            ) : (
                              member.name
                            )}
                            {member.note && <span className="text-stone-500 font-normal text-sm"> ({member.note})</span>}
                          </p>
                          <p className="text-sm text-stone-600">{member.role}</p>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};
